import { memo, useState } from 'react';
import { Button } from '@flintmint/web-shared/components/button';
import { Checkbox } from '@flintmint/web-shared/components/checkbox';
import { Alert } from '@flintmint/web-shared/components/alert';
import { Switch } from '@flintmint/web-shared/components/switch';
import type { ModuleType } from '@flintmint/core-shared/firestore/schemas';

import type { MedicalModuleWithId, MedicalModuleInput } from '@/services/medical-modules-service';
import type { FlowWithId } from '@/services/flows-service';
import { formatUpdatedInfo } from '@/utils/format-values';
import { normalizeModuleName } from '@flintmint/core-shared/medical-note';
import { DUPLICATE_MODULE_NAME_ERROR, isModuleNameTaken } from '../../module-name';
import { FlowChipsCell } from './flow-chips-cell';

import styles from './module-row.module.scss';

const TYPE_LABELS: Record<ModuleType, string> = {
  complaint: 'Complaint',
  procedure: 'Procedure',
  other: 'Other',
};

interface ModuleRowProps {
  module: MedicalModuleWithId;
  flows: FlowWithId[];
  moduleIdsByName: ReadonlyMap<string, readonly string[]>;
  /** Resolves to whether the save succeeded; the caller reports the outcome. */
  onSave: (module: MedicalModuleWithId, data: Partial<MedicalModuleInput>) => Promise<boolean>;
  onDelete: (module: MedicalModuleWithId) => Promise<void>;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  showType?: boolean;
  availableTypes?: ModuleType[];
}

type ModuleEditData = {
  type: ModuleType;
  name: string;
  content: string;
  criteria: string;
};

const toEditData = (module: MedicalModuleWithId): ModuleEditData => ({
  type: module.type,
  name: module.name,
  content: module.content,
  criteria: module.criteria ?? '',
});

// Memoized: filter keystrokes re-render the section, and rows only change when their module, selection or the flows do.
export const ModuleRow = memo(function ModuleRow({
  module,
  flows,
  moduleIdsByName,
  onSave,
  onDelete,
  selected,
  onToggleSelect,
  showType,
  availableTypes = [],
}: ModuleRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editData, setEditData] = useState<ModuleEditData>(toEditData(module));

  // Only a rename is checked, so an existing duplicate doesn't block unrelated edits to either module.
  const isRenamed = normalizeModuleName(editData.name) !== normalizeModuleName(module.name);
  const isNameTaken = isRenamed && isModuleNameTaken(editData.name, moduleIdsByName, module.id);
  const isValid = editData.name.trim() !== '' && !isNameTaken;

  const handleSave = async () => {
    if (!isValid) return;
    setIsSaving(true);
    try {
      const saved = await onSave(module, { ...editData, name: editData.name.trim(), criteria: editData.criteria.trim() });
      if (saved) setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = () => {
    setEditData(toEditData(module));
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleDelete = async () => {
    await onDelete(module);
  };

  const selectCheckbox = (
    <Checkbox
      checked={selected}
      onChange={() => onToggleSelect(module.id)}
      aria-label={`Select ${module.name}`}
      data-testid="module-select-checkbox"
    />
  );

  if (isEditing) {
    return (
      <div
        className={styles.row}
        data-editing
        data-selected={selected || undefined}
        data-has-type={showType || undefined}
        data-testid="module-row"
        data-name={module.name}
      >
        {selectCheckbox}
        <div className={styles.field}>
          <input
            className={styles.nameInput}
            value={editData.name}
            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
            placeholder="Name"
            aria-invalid={isNameTaken}
            data-testid="module-name-input"
          />
          {isNameTaken && (
            <p className={styles.fieldError} data-testid="module-name-error">
              {DUPLICATE_MODULE_NAME_ERROR}
            </p>
          )}
        </div>
        {showType && (
          <select
            className={styles.typeSelect}
            value={editData.type}
            onChange={(e) => setEditData({ ...editData, type: e.target.value as ModuleType })}
            data-testid="module-type-select"
          >
            {availableTypes.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        )}
        <textarea
          className={styles.criteriaTextarea}
          value={editData.criteria}
          onChange={(e) => setEditData({ ...editData, criteria: e.target.value })}
          placeholder="Criteria (e.g. patient age 65+)"
          rows={2}
          data-testid="module-criteria-input"
        />
        <textarea
          className={styles.contentTextarea}
          value={editData.content}
          onChange={(e) => setEditData({ ...editData, content: e.target.value })}
          placeholder="Content"
          rows={3}
          data-testid="module-content-input"
        />
        <span />
        <span />
        <span className={styles.updated} />
        <div className={styles.actions}>
          <Button
            size="small"
            color="success"
            onClick={handleSave}
            loading={isSaving}
            disabled={!isValid}
            appearance="outline"
            data-testid="module-save-btn"
          >
            Save
          </Button>
          <Button size="small" onClick={handleCancel} disabled={isSaving} appearance="outline" data-testid="module-cancel-btn">
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles.row}
      data-selected={selected || undefined}
      data-has-type={showType || undefined}
      data-testid="module-row"
      data-name={module.name}
    >
      {selectCheckbox}
      <span className={styles.name}>{module.name}</span>
      {showType && <span className={styles.type}>{TYPE_LABELS[module.type]}</span>}
      <span className={styles.criteria}>{module.criteria || '—'}</span>
      <span className={styles.content}>{module.content}</span>
      <FlowChipsCell
        flowIds={module.flowIds ?? []}
        flows={flows}
        onChange={async (flowIds) => void (await onSave(module, { flowIds }))}
      />
      <Switch
        checked={module.enabled !== false}
        onChange={(enabled) => void onSave(module, { enabled })}
        data-testid="module-enabled-toggle"
      />
      <span className={styles.updated}>{formatUpdatedInfo(module.updatedAt, module.updatedBy)}</span>
      <div className={styles.actions}>
        <Button size="small" onClick={handleEdit} appearance="outline" color="light" data-testid="module-edit-btn">
          Edit
        </Button>
        <Alert
          type="danger"
          title="Module"
          message={`Are you sure you want to delete "${module.name}"?`}
          primaryButton={{
            onClick: handleDelete,
            appearance: 'outline',
          }}
          secondaryButton={{
            appearance: 'outline',
          }}
          trigger={
            <Button size="small" color="danger" appearance="outline" data-testid="module-delete-btn">
              Delete
            </Button>
          }
        />
      </div>
    </div>
  );
});
